/**
 * useBigwaConnector.ts
 * Hook para receber referências do Bigwa Connector (extensão Chrome)
 * 
 * Bigwa © 2025
 */

import { useEffect } from 'react';
import { logger } from '../utils/logger';
import { useLibraryStore } from '../stores/libraryStore';
import { Work, WorkType, Author } from '../types/work';
import { nanoid } from 'nanoid';
import { bigwaToast } from '../utils/bigwaToast';

// Dados recebidos da extensão Chrome
interface ConnectorReference {
  title: string;
  authors?: string;
  year?: string;
  journal?: string;
  doi?: string;
  abstract?: string;
  sourceUrl?: string;
  sourceDomain?: string;
  itemType?: string;
  capturedAt?: string;
}

// Converter string de autores para array de Author
function parseAuthors(authorsStr?: string): Author[] {
  if (!authorsStr || !authorsStr.trim()) {
    return [{ firstName: '', lastName: 'Autor desconhecido' }];
  }
  
  // Separar por ; ou ,
  const authorNames = authorsStr.split(/[;,]/).map(s => s.trim()).filter(s => s);
  
  return authorNames.map(name => {
    const parts = name.split(' ').filter(p => p);
    if (parts.length === 0) {
      return { firstName: '', lastName: 'Desconhecido' };
    }
    if (parts.length === 1) {
      return { firstName: '', lastName: parts[0] };
    }
    // Último nome é sobrenome, resto é primeiro nome
    const lastName = parts.pop() || '';
    const firstName = parts.join(' ');
    return { firstName, lastName };
  });
}

// Mapear tipo do Connector para WorkType do Bigwa
function mapItemType(type?: string): WorkType {
  switch (type) {
    case 'article': return 'article';
    case 'book': return 'book';
    case 'thesis': return 'thesis';
    case 'conference': return 'conference';
    case 'report': return 'report';
    case 'webpage': return 'website';
    case 'preprint': return 'article';
    default: return 'article';
  }
}

export function useBigwaConnector() {
  const addWork = useLibraryStore((state) => state.addWork);

  useEffect(() => {
    const handleConnectorImport = async (event: CustomEvent<ConnectorReference>) => {
      const data = event.detail;
      
      if (!data || !data.title) {
        logger.warn('BigwaConnector', 'Dados inválidos recebidos');
        return;
      }


      const now = new Date().toISOString();
      const yearNum = data.year ? parseInt(data.year, 10) : new Date().getFullYear();

      // Criar Work completo compatível com libraryStore
      const newWork: Work = {
        id: nanoid(),
        type: mapItemType(data.itemType),
        title: data.title,
        authors: parseAuthors(data.authors),
        year: isNaN(yearNum) ? new Date().getFullYear() : yearNum,
        abstract: data.abstract || '',
        doi: data.doi || '',
        url: data.sourceUrl || '',
        journal: data.journal || '',
        notes: `Importado via Bigwa Connector de ${data.sourceDomain || 'web'} em ${new Date().toLocaleDateString('pt-BR')}`,
        tags: [],
        format: 'digital',
        readingStatus: 'unread',
        createdAt: now,
        updatedAt: now,
      };

      try {
        // Adicionar à biblioteca
        await addWork(newWork);

        // Feedback visual
        const shortTitle = data.title.length > 40 
          ? data.title.substring(0, 40) + '...' 
          : data.title;
        
        bigwaToast.notify(`"${shortTitle}" adicionado!`);
      } catch (error) {
        logger.error('[Bigwa Connector] Erro ao adicionar:', error);
        bigwaToast.error('Erro ao importar referência');
      }
    };

    // Registrar listener para evento do Connector
    window.addEventListener('bigwaConnectorImport', handleConnectorImport as unknown as EventListener);


    return () => {
      window.removeEventListener('bigwaConnectorImport', handleConnectorImport as unknown as EventListener);
    };
  }, [addWork]);
}
